import type { Brand, Model } from '@studio/models/device';
import { useLatestFn } from '@studio/react-utils/hooks';
import { useCallback, useEffect, useRef } from 'react';
import type { T_Maybe, T_Optional } from '@tryiris-ai/js-types';
import type { Selection } from './types.js';
import type { IdentifiedBy } from '#~/analytics/domains/device-identification.js';
import type { Collection } from '#~/api/device/registry/index.js';
import type { Template } from '#~/device/registry/index.js';
import { getNickname } from '#~/device/registry/index.js';

/** @internal */
export const getDefaultName = ({
  brands,
  models,
  selection
}: T_Optional<{
  brands: Collection<Brand>;
  models: Collection<Model>;
  selection: Selection;
}>): T_Maybe<string> => {
  const brand = selection?.brandId ? brands?.items[selection.brandId] : undefined;
  const model = selection?.modelId ? models?.items[selection.modelId] : undefined;

  return brand && model ? getNickname(brand, model) : undefined;
};

/**
 * Initial brand/model selection: the resolved template's model (full match),
 * else the brand-only discovery seed (ENG-4830 A2), else nothing.
 *
 * @internal
 */
export const getInitialSelection = (config?: Template, seededBrandId?: Brand['id']): Selection =>
  config
    ? { brandId: config.model.brandId, modelId: config.model.id }
    : seededBrandId
      ? { brandId: seededBrandId }
      : {};

/** Which fields carry the "Detected" pill. */
export interface Detected {
  brand: boolean;
  model: boolean;
}

/**
 * "Detected" pills for the brand/model step (ENG-4830 A1/A2).
 *
 * Source of truth is what discovery already resolved when the step opened: a
 * full fingerprint match sets `candidate.modelId` (both pills), a brand-only
 * match seeds `source.seededBrandId` (brand pill). Never guesses: when the
 * operator already chose the current values earlier in this add
 * (`identifiedBy` is a user answer) the values are theirs, not discovery's,
 * so no pill shows. A field the operator changed on this step loses its pill.
 *
 * @internal
 */
export const getDetected = (
  {
    identifiedBy,
    modelId,
    seededBrandId
  }: {
    identifiedBy?: T_Maybe<IdentifiedBy>;
    modelId?: T_Maybe<Model['id']>;
    seededBrandId?: T_Maybe<Brand['id']>;
  },
  changed: Partial<Detected> = {}
): Detected => {
  // Only discovery (or no answer yet) leaves the detected pills on; every
  // operator answer (`searched`, `not_listed`, ...) is theirs, not detected.
  const fromDiscovery = identifiedBy === undefined || identifiedBy === 'detected';

  return {
    brand: fromDiscovery && Boolean(modelId || seededBrandId) && !changed.brand,
    model: fromDiscovery && Boolean(modelId) && !changed.model
  };
};

/**
 * `identifiedBy` for a supported model the operator picked on this step
 * (ENG-4833): `brand_detected_then_searched` when discovery had resolved only
 * the brand (still detected at pick time, no detected model) and the operator
 * searched the model within it; else a plain `searched` pick — including when
 * the pick replaced a detected model, which the ticket folds into `searched`
 * (no separate bucket). A not-yet-supported pick never gets here: it opens C2,
 * whose Continue records `not_yet_supported_picked` (ENG-4924).
 *
 * @internal
 */
export const getPickedIdentifiedBy = (brandOnlyDetected: boolean): IdentifiedBy =>
  brandOnlyDetected ? 'brand_detected_then_searched' : 'searched';

/**
 * Brand of a model, from whatever model collections are already loaded.
 *
 * @internal
 */
export const findBrandIdForModel = (
  modelsByBrand: T_Maybe<Readonly<Record<string, T_Maybe<Collection<Model>>>>>,
  modelId: T_Maybe<Model['id']>
): T_Maybe<Brand['id']> => {
  if (!modelId) {
    return undefined;
  }

  return Object.values(modelsByBrand ?? {}).find((models) => models?.items[modelId])?.items[modelId]
    ?.brandId;
};

/**
 * Follow `candidate.modelId` when something other than this step writes it
 * (ENG-4832: the not-listed flow's Continue writes the generic Other/Other
 * model, its "Did you mean" writes the picked model).
 *
 * The brand/model steps mirror the selection in local state, seeded once, so
 * without this they keep showing — and resolving the template of — the old
 * model. The step reports each id it writes itself via the returned
 * `markWritten`; only a store value it did not write calls `onExternalChange`,
 * so a local pick can never bounce back as a spurious reset (no render-order
 * race between the local `setState` and the store update).
 *
 * @param storeModelId - Live `candidate.modelId`.
 * @param onExternalChange - Called with the new id after an outside write.
 * @returns `markWritten(id)`: call before the step writes `candidate.modelId`.
 * @internal
 */
export const useExternalModelId = (
  storeModelId: T_Maybe<Model['id']>,
  onExternalChange: (modelId: T_Maybe<Model['id']>) => void
): ((modelId: T_Maybe<Model['id']>) => void) => {
  // Last value this step has accounted for: the mount value, its own writes,
  // and external values it has already followed.
  const known = useRef(storeModelId);
  const notify = useLatestFn(onExternalChange);

  useEffect(() => {
    if (storeModelId === known.current) {
      return;
    }

    known.current = storeModelId;
    notify(storeModelId);
  }, [storeModelId]);

  return useCallback((modelId: T_Maybe<Model['id']>) => {
    known.current = modelId;
  }, []);
};
